<template lang="pug">
  .log-post
    .log-post-content
      .log-post-header
        EntityAvatar(:image="$auth.user().avatar" size-class="is-48x48" is-rounded)
        .log-post-username {{ $auth.user().name }}
        .log-post-header-actions
          a.add-photo-icon(@click="handleAddPhoto" role="button" aria-label="Add a photo")
            BIcon(icon="image-plus")
          BDropdown.visibility-menu(position="is-bottom-left" aria-role="menu")
            a.visibility-trigger(
              slot="trigger"
              role="button"
              aria-label="Post visibility"
            )
              // key forces a re-render: WtSvgIcon is v-once and ignores prop changes
              WtSvgIcon(:key="visibilityIcon" :icon="visibilityIcon" width="24px" height="24px")
            BDropdownItem.visibility-option(
              v-for="option in visibilityOptions"
              :key="option.code"
              aria-role="menuitem"
              :class="{ 'is-selected': option.code === visibility }"
              @click="visibility = option.code"
            )
              span {{ option.label }}

      .log-post-text-input
        BInput(
          maxlength="1000"
          type="textarea"
          v-model="postText"
          placeholder="Write something..."
          :has-counter="false"
        )

      .photo-upload-container(v-if="showPhotoUpload && !previewImageData")
        BField
          BUpload.photo-upload(
            drag-drop
            native
            expanded
            accept="image/jpeg, image/gif, image/png"
            @input="previewImage"
          )
            .file-uploader
              .file-contents
                span.file-label Drop your files here or click to upload
                BIcon.file-icon.is-medium(icon="upload")
        p.has-text-danger.has-text-centered(v-if="imageCompressorError")
          | There was a problem reading that image. Please try another one.

      .preview-image-container(v-if="previewImageData")
        .preview-thumb
          img.preview-image(:src="previewImageData" alt="Your Photo")
          a.remove-image-btn(@click="removeImage" role="button" aria-label="Remove photo")
            BIcon(icon="close" size="is-small")

      .submit-container
        WtButton.submit-post-button(
          @click.native="submitPost"
          :loading="savingPost"
          :disabled="!canSubmit"
        ) Submit
</template>

<script>
import imageCompression from "browser-image-compression";
import { mapActions } from "vuex";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import EntityAvatar from "@/components/WtEntityAvatar/WtEntityAvatar";
import WtButton from "@@/WtButton/WtButton.vue";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon.vue";

const VISIBILITY_OPTIONS = [
  { code: 0, label: "Only Me", icon: "eye-off" },
  { code: 1, label: "Friends", icon: "eye-friends" },
  { code: 2, label: "Anyone", icon: "eye" }
];

export default {
  name: "LogPost",
  components: { EntityAvatar, WtButton, WtSvgIcon },

  mixins: [analyticsTrackingMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      postText: "",
      previewImageData: "",
      showPhotoUpload: false,
      imageCompressorError: false,
      visibility: 2,
      savingPost: false,
      newPhoto: null,
      originalImageUrl: "",
      isRemoteImageDeleted: false
    };
  },

  computed: {
    visibilityOptions() {
      return VISIBILITY_OPTIONS;
    },

    visibilityIcon() {
      const selected = VISIBILITY_OPTIONS.find(
        option => option.code === this.visibility
      );
      return this._.get(selected, "icon", "eye");
    },

    currentTextValue() {
      return this._.get(this.entryItem, "text", "");
    },
    currentVisibility() {
      return this._.get(this.entryItem, "visibility", 2);
    },
    currentImagePreview() {
      return this._.get(this.entryItem, "image", "");
    },

    setFormPostText() {
      if (!this._.isEmpty(this.entryItem)) {
        return this.entryItem.text !== this.postText;
      }
      return true;
    },

    setFormVisibility() {
      if (!this._.isEmpty(this.entryItem)) {
        return this.entryItem.visibility !== this.visibility;
      }
      return true;
    },

    setFormPreviewImageData() {
      if (!this._.isEmpty(this.entryItem)) {
        return this.entryItem.image !== this.previewImageData;
      }
      return true;
    },

    hasChanges() {
      const entry = this.entryItem;
      return (
        this.postText.trim() !== (entry.text || "").trim() ||
        this.visibility !== (entry.visibility ?? 2) ||
        (entry.image && !this.previewImageData && !this.isRemoteImageDeleted) ||
        (!entry.image && this.previewImageData)
      );
    },

    canSubmit() {
      return this.hasChanges && !this.savingPost;
    }
  },

  watch: {
    postText() {
      if (this.setFormPostText) {
        this.formData.set("text", this.postText);
        this.formData.set("textVisibility", this.visibility);
        this.$emit("formDataUpdated", true);
      }
    },
    previewImageData() {
      if (this.setFormPreviewImageData) {
        if (this.newPhoto) {
          this.formData.set("image", this.newPhoto);
        }
        this.$emit("formDataUpdated", true);
      }
    },
    visibility() {
      if (this.setFormVisibility) {
        this.formData.set("visibility", this.visibility);
        this.formData.set("textVisibility", this.visibility);
        this.$emit("formDataUpdated", true);
      }
    },

    entryItem: {
      handler() {
        this.postText = this.currentTextValue;
        this.previewImageData = this.currentImagePreview;
        this.visibility = this.currentVisibility;
      },
      immediate: true,
      deep: true
    },

    uniqueKey: {
      handler() {
        this.postText = this.currentTextValue;
        this.previewImageData = this.currentImagePreview;
        this.visibility = this.currentVisibility;
      },
      immediate: true
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_post");
    this.resetFormFromEntity(this.entryItem);
  },

  methods: {
    ...mapActions("members", ["deleteJournalPhoto"]),
    ...mapActions("feed", ["storeFeedPost"]),

    resetFormFromEntity(entry) {
      this.postText = this._.get(entry, "text", "");
      this.visibility = this._.get(entry, "visibility", 2);
      this.originalImageUrl = this._.get(entry, "image", "");
      this.previewImageData = this.originalImageUrl;
      this.newPhoto = null;
      this.showPhotoUpload = false;
      if (this.previewImageData !== "") {
        this.showPhotoUpload = true;
      }
      this.imageCompressorError = false;

      if (this.isRemoteImageDeleted) {
        this.newPhoto = null;
        this.previewImageData = "";
      }
    },

    handleAddPhoto() {
      this.showPhotoUpload = true;
    },

    submitPost() {
      this.savingPost = true;
      this.saveEntryWrapper(this.storeFeedPost, this.storeFeedPost).finally(
        () => {
          this.savingPost = false;
        }
      );
    },

    async removeImage() {
      if (this.entryItem.id && this.entryItem.image) {
        const photoData = this.newPhoto;
        const previewImageUrl = this.previewImageData;
        try {
          this.previewImageData = "";
          this.newPhoto = null;
          this.showPhotoUpload = true;
          await this.deleteJournalPhoto({ itemId: this.entryItem.id });
        } catch (error) {
          this.newPhoto = photoData;
          this.previewImageData = previewImageUrl;
          this.showPhotoUpload = false;
          return;
        }
      } else {
        this.previewImageData = "";
        this.newPhoto = null;
        this.showPhotoUpload = true;
      }

      if (this.formData.has("image")) {
        const item = "image";
        this.formData.delete(item);
      }
      this.isRemoteImageDeleted = true;

      this.$emit("formDataUpdated", true);
    },

    async previewImage(file) {
      const options = {
        maxSizeMB: 10,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };

      try {
        this.imageCompressorError = false;
        const blob = await imageCompression(file, options);

        this.newPhoto = blob;
        this.previewImageData = await imageCompression.getDataUrlFromFile(blob);
        this.showPhotoUpload = true;
      } catch (error) {
        this.imageCompressorError = true;
        this.showPhotoUpload = true;
      }
    }
  }
};
</script>

<style scoped lang="sass">
.log-post-content
  display: flex
  flex-direction: column
  gap: 16px

  .log-post-header
    display: flex
    flex-direction: row
    align-items: center
    gap: 12px
    .log-post-username
      flex: 1
      font-weight: 600
      font-size: 16px
      line-height: 24px
      color: #1B1D20
    .log-post-header-actions
      display: flex
      align-items: center
      gap: 16px
      ::v-deep .WtSvgIcon
        line-height: 0
        svg
          display: block
      ::v-deep .dropdown-trigger
        display: flex
        align-items: center
      .add-photo-icon
        display: flex
        align-items: center
        color: #6E7074
      .visibility-trigger
        display: flex
        align-items: center
        cursor: pointer
      .visibility-menu
        ::v-deep .dropdown-content
          border-radius: 12px
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
          padding: 8px
        ::v-deep .dropdown-item
          border-radius: 8px
          text-align: center
          color: #2972F0
          font-size: 14px
          font-weight: 400
          line-height: 1.4
          padding: 8px 24px
          &.is-selected
            background: #C6DCFF

  .log-post-text-input
    ::v-deep textarea
      border: none
      box-shadow: none
      resize: none
      min-height: 160px
      font-weight: 400
      font-size: 16px
      line-height: 24px
      padding-left: 0
      &:focus
        border: none
        box-shadow: none
        outline: none
    ::v-deep .b-input-counter
      display: none !important

  .file-uploader
    display: flex
    align-items: center
    justify-content: center
    width: 100%
  .file-contents
    display: flex
    flex-direction: row
    gap: 8px
    align-items: center
  .file-label
    font-weight: 400
    font-size: 15px
    line-height: 23px
    letter-spacing: 0px

  .preview-image-container
    display: flex
    .preview-thumb
      position: relative
      display: inline-flex
      .preview-image
        width: 139px
        max-height: 160px
        object-fit: cover
        border-radius: 8px
        display: block
      .remove-image-btn
        position: absolute
        top: -8px
        right: -8px
        display: flex
        align-items: center
        justify-content: center
        width: 28px
        height: 28px
        border-radius: 50%
        background: #FFFFFF
        color: #1B1D20
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2)

  .submit-container
    display: flex
    flex-direction: column
    border-top: 1px solid #E8EBEF
    padding-top: 16px
    .submit-post-button
      background-color: #2972F0
      color: #FFFFFF
      border-radius: 10px
      font-size: 16px
      font-weight: 600
      height: 44px
      border: none
</style>
